import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Alert,
} from '@mui/material';
import { getAcceptanceHistory, formatVersion } from '../utils/legalApi';
import type { LegalAcceptanceRecord } from '../utils/legalApi';

const DOCUMENT_LABELS: Record<string, string> = {
  tos: 'Terms of Service',
  privacy: 'Privacy Policy',
};

export const LegalAgreementsSection: React.FC = () => {
  const [records, setRecords] = useState<LegalAcceptanceRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    getAcceptanceHistory()
      .then(data => {
        setRecords(data);
        setIsLoading(false);
      })
      .catch(() => {
        setError(true);
        setIsLoading(false);
      });
  }, []);

  const formatDate = (isoDate: string): string => {
    return new Date(isoDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Box>
      <Typography variant="h6">Legal Agreements</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Your document acceptance history
      </Typography>
      <Card variant="outlined">
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Alert severity="error">Unable to load acceptance history.</Alert>
        )}
        {!isLoading && !error && records.length === 0 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No acceptance records found.
            </Typography>
          </Box>
        )}
        {!isLoading && !error && records.length > 0 && (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Document</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Accepted Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map((record, index) => (
                <TableRow key={`${record.documentType}-${record.acceptedAt}-${index}`}>
                  <TableCell>
                    {DOCUMENT_LABELS[record.documentType] ?? record.documentType}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{formatVersion(record.version)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{formatDate(record.acceptedAt)}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </Box>
  );
};
